import { enforce, omitWhen, test } from 'vest';

import {
  isEndpointUrl,
  withDefaults,
} from 'admin-panel/utils/integrations/config';

import type { IntegrationConfig } from 'admin-panel/models/integrations/configuration';
import type {
  IntegrationFormValues,
  ServiceFormDefinition,
} from 'admin-panel/utils/integrations/types';

export interface MewsService {
  apiBaseUrl: string;
  clientToken: string;
  accessToken: string;
  languageTag: string;
  reservationServiceIds: string[];
  useGrossAmount: boolean;
  enterpriseId: string;
}

export interface MewsGreenOptionRequest {
  state: 'enabled' | 'disabled';
  confirmationMode: 'manual' | 'automatic';
  sendAsOrder: boolean;
  sendAsTasks: boolean;
  serviceId: string;
  departmentId: string;
}

const LANGUAGE_TAG = /^[a-z]{2}(-[A-Z][a-z]{3})?-[A-Z]{2}$/;

const SERVICE = 'currentConfig.service';
const GREEN_OPTION = 'currentConfig.features.greenOptionRequest';

const defaultConfig = (): IntegrationConfig => ({
  service: {
    apiBaseUrl: 'https://api.mews.com',
    clientToken: '',
    accessToken: '',
    languageTag: '',
    reservationServiceIds: [],
    useGrossAmount: false,
    enterpriseId: '',
  },
  features: {
    greenOptionRequest: {
      state: 'disabled',
      confirmationMode: 'automatic',
      sendAsOrder: false,
      sendAsTasks: false,
      serviceId: '',
      departmentId: '',
    },
  },
  cron: [],
  webhooks: [{ name: 'processor', enabled: true }],
});

export const mews: ServiceFormDefinition = {
  toFormConfig: (config) => withDefaults(defaultConfig(), config),

  validate(values: IntegrationFormValues, t) {
    const service = values.currentConfig.service as unknown as MewsService;
    const { features } = values.currentConfig;
    const greenOption = (features['greenOptionRequest'] ??
      {}) as MewsGreenOptionRequest;

    test(`${SERVICE}.apiBaseUrl`, t('validations.required'), () => {
      enforce(service.apiBaseUrl).isNotBlank();
    });

    omitWhen(!service.apiBaseUrl, () => {
      test(`${SERVICE}.apiBaseUrl`, t('validations.url'), () => {
        enforce(isEndpointUrl(service.apiBaseUrl)).isTruthy();
      });
    });

    test(`${SERVICE}.clientToken`, t('validations.required'), () => {
      enforce(service.clientToken).isNotBlank();
    });

    test(`${SERVICE}.accessToken`, t('validations.required'), () => {
      enforce(service.accessToken).isNotBlank();
    });

    test(`${SERVICE}.languageTag`, t('validations.required'), () => {
      enforce(service.languageTag).isNotBlank();
    });

    omitWhen(!service.languageTag, () => {
      test(`${SERVICE}.languageTag`, t('validations.invalid'), () => {
        enforce(service.languageTag).matches(LANGUAGE_TAG);
      });
    });

    test(`${SERVICE}.reservationServiceIds`, t('validations.required'), () => {
      enforce(service.reservationServiceIds).isNotEmpty();
      enforce(
        service.reservationServiceIds.every((id) => Boolean(id?.trim()))
      ).isTruthy();
    });

    omitWhen(!features['greenOptionRequest'], () => {
      test(`${GREEN_OPTION}.state`, t('validations.invalid'), () => {
        enforce(greenOption.state).inside(['enabled', 'disabled']);
      });
    });

    omitWhen(greenOption.state !== 'enabled', () => {
      test(
        `${GREEN_OPTION}.confirmationMode`,
        t('validations.required'),
        () => {
          enforce(greenOption.confirmationMode).inside(['manual', 'automatic']);
        }
      );
    });

    omitWhen(
      greenOption.state !== 'enabled' || !greenOption.sendAsOrder,
      () => {
        test(`${GREEN_OPTION}.serviceId`, t('validations.required'), () => {
          enforce(greenOption.serviceId).isNotBlank();
        });
      }
    );
  },
};
